import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import PhoneNumberInput from '@/components/PhoneNumberInput';
import { updateUserProfile } from '@/store/slices/meSlice';
import { EMAIL_RULES, FIRST_NAME_RULES, LAST_NAME_RULES, PHONE_NUMBER_RULES } from '@/utils/validationRules';
import { Button, FormLabel, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ChangePasswordModal from './ChangePasswordModal';
import { updateAccountInfo } from './data/updateAccountInfoService';

const ProfileSettings = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { userProfile } = useSelector(state => state.me);
  const { control, handleSubmit, reset, formState } = useForm({
    defaultValues: useMemo(() => {
      return userProfile;
    }, [userProfile]),
  });

  useEffect(() => {
    reset(userProfile);
  }, [reset, userProfile]);

  const [openChangePassword, setOpenChangePassword] = useState(false);
  const handleOpenChangePasswordModal = () => setOpenChangePassword(true);
  const handleCloseChangePasswordModal = () => setOpenChangePassword(false);

  const onSubmit = async data => {
    setLoading(true);
    try {
      const response = await updateAccountInfo(data);
      dispatch(updateUserProfile(data));
      toast.success(response?.data?.message ?? 'Account information updated');
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error('Something went wrong');
      return;
    }
  };

  const handleReset = event => {
    event?.preventDefault?.();
    reset();
  };

  return (
    <>
      <LoadingBackdropWrapper backdropOpen={loading}>
        <div className='font-[Manrope] mb-[34px]'>
          <div className='font-semibold text-[24px]'>Account Settings</div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} onReset={handleReset}>
          <div className='grid grid-cols-2 gap-x-[34px] gap-y-[16px]'>
            <div>
              <FormLabel htmlFor='first-name-input'>First Name</FormLabel>
              <Controller
                name='firstName'
                control={control}
                rules={FIRST_NAME_RULES}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id='first-name-input'
                    autoComplete='given-name'
                    type='text'
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      htmlInput: { placeholder: 'First Name' },
                    }}
                  />
                )}
              />
            </div>
            <div>
              <FormLabel htmlFor='last-name-input'>Last Name</FormLabel>
              <Controller
                name='lastName'
                control={control}
                rules={LAST_NAME_RULES}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id='last-name-input'
                    autoComplete='family-name'
                    type='text'
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      htmlInput: { placeholder: 'hi@example.com' },
                    }}
                  />
                )}
              />
            </div>
            <div>
              <FormLabel htmlFor='email-input'>Email</FormLabel>
              <Controller
                name='email'
                control={control}
                rules={EMAIL_RULES}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id='email-input'
                    autoComplete='email'
                    type='text'
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      htmlInput: { placeholder: 'hello@example.com' },
                    }}
                  />
                )}
              />
            </div>
            <div>
              <FormLabel htmlFor='phone-input'>Phone Number</FormLabel>
              <Controller
                name='phone'
                control={control}
                rules={PHONE_NUMBER_RULES}
                render={({ field, fieldState: { error } }) => (
                  <PhoneNumberInput
                    {...field}
                    fullWidth
                    id='phone-input'
                    type='text'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </div>
          </div>
          <hr className='-mx-[35px] mt-[48px] mb-[28px]' />
          <div>
            <div className='font-semibold text-[18px]'>Password</div>
            <Button
              variant='contained'
              sx={{
                display: 'block',
                marginTop: '25px',
              }}
              type='button'
              onClick={handleOpenChangePasswordModal}
            >
              Change Password
            </Button>
          </div>
          <hr className='-mx-[35px] mt-[42px] mb-[14px]' />
          <div className='flex justify-end gap-[18px]'>
            <Button variant='outlined' type='reset'>
              Cancel
            </Button>
            <Button type='submit' variant='contained' disabled={!formState.isDirty}>
              Save
            </Button>
          </div>
        </form>
      </LoadingBackdropWrapper>
      <ChangePasswordModal
        email={userProfile.email}
        open={openChangePassword}
        handleClose={handleCloseChangePasswordModal}
      />
    </>
  );
};

export default ProfileSettings;
